import { createSlice } from '@reduxjs/toolkit';
import { SiteId } from 'wly-ecommerce-common';

import {
  IBillingInfo,
  ICorPromoCodes,
  IOrder,
  IPaybPayment,
  Discount,
  DiscountType,
  DigitalFormat,
  BillInformation,
} from '@global/types/types';
import { STATUSES } from '@pages/checkout/types';

import * as Constants from '@global/App/constants/constants';

import {
  initBillingInfo,
  initOrderPrice,
  initShippingInfo,
} from '@pages/checkout/consts';
import {
  getOrderItems,
  getOrderPrice,
  OrderItemType,
  OrderPriceType,
  convertDiscounts,
  convertDeliveryOptions,
  PreviewDeliveryOption,
  getSubscriptionNextBillingDate,
  getSubscriptionFrequency,
} from '@global/utils/orderUtils';
import { mapShippingToView, mapToView } from '@pages/checkout/helper';
import { checkIsGuestAllowed, redirectGuestToAuth } from '@global/utils/guest';
import { clearTokensFromCookies } from '@global/hooks/useAuth/utils';

import { getSubscriptionsForCheckout } from '@src/global/store/slices/cart/extraReducers/getSubscriptionsForCheckout';
import { getCartData } from '@global/store/slices/cart/extraReducers/getCartData';
import { updateBillingInformation } from '@global/store/slices/cart/extraReducers/updateBillingInformation';
import { updateWpsPaymentInformation } from '@global/store/slices/cart/extraReducers/saveWpsPayment';
import { getTaxData } from '@global/store/slices/cart/extraReducers/getTaxData';
import { isCartHasUnavailableProducts } from '@global/store/slices/cart/extraReducers/isCartHasUnavailableProducts';
import { IShippingInfo } from '@pages/checkout/components/ShippingInformation/types';
import { updateShippingInformation } from './extraReducers/updateShippingInformation';
import { getDeliveryOptions } from '@global/store/slices/cart/extraReducers/getDeliveryOptions';
import { SiteConfig } from '@global/utils/SiteConfig';

export interface CartSliceState
  extends Omit<IOrder, 'biCreatedAt' | 'id' | 'hiConsistsOf' | 'lastBillBiId'> {
  isLoading: boolean;
  isShowSpinner: boolean;
  wSiteId: SiteId;

  isCheckSubscriptions: STATUSES;
  updatedWithNewCustomer: STATUSES;
  updateBillingInformation: STATUSES;
  updatePaymenInformation: STATUSES;
  istaxCalculated: STATUSES;
  isCartReady?: boolean;
  isGuestOrder: boolean;

  skus: (string | null)[];
  cartId: string | null;
  uid: string | null;
  error: string | null;
  isSubscriptionExist: boolean;
  isFreeTrial: boolean;
  isTextbook?: boolean;
  isPhysical?: boolean;
  isSubscription?: boolean;
  isPrintOnDemand?: boolean;
  nextBillDate?: string;
  subscriptionFrequency?: string;
  orderPrice: OrderPriceType;
  billingInfo: IBillingInfo;
  shippingInfo: IShippingInfo;
  paymentInfo?: IPaybPayment;
  orderItems: OrderItemType[];
  promotions: ICorPromoCodes[] | null;

  agreementCheck: boolean;
  schoolFiceId: string;

  discounts: Discount[];
  deliveryOptions: PreviewDeliveryOption[];
  lastBillBiId?: string | null;
  billFromOrder?: BillInformation;
}

const initialState: CartSliceState = {
  isLoading: false,
  isShowSpinner: false,
  wSiteId: SiteId.WILEYCOM,

  isCheckSubscriptions: STATUSES.FALSE,
  updatedWithNewCustomer: STATUSES.FALSE,
  updateBillingInformation: STATUSES.FALSE,
  updatePaymenInformation: STATUSES.FALSE,
  istaxCalculated: STATUSES.FALSE,
  isPhysical: false,
  isCartReady: false,
  isGuestOrder: false,

  skus: null,
  error: null,
  cartId: null,
  uid: null,
  biId: null,
  isSubscriptionExist: false,

  orderPrice: initOrderPrice,
  billingInfo: initBillingInfo,
  shippingInfo: initShippingInfo,
  orderItems: [],
  promotions: null,

  agreementCheck: false,
  pricPrice: { edges: [] },
  schoolFiceId: '',
  isSubscription: false,
  isFreeTrial: false,
  nextBillDate: null,
  subscriptionFrequency: null,

  discounts: [],
  deliveryOptions: [],
  lastBillBiId: null,
  billFromOrder: null,
};
export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartId: (state, { payload }) => {
      state.cartId = payload;
    },
    toggleAgreementCheck: (state) => {
      state.agreementCheck = !state.agreementCheck;
    },
    setUpdateBillingInformation: (state, { payload }) => {
      state.updateBillingInformation = payload;
    },
    setUpdateWpsPaymentInformation: (state, { payload }) => {
      state.updatePaymenInformation = payload;
    },
    setSku: (state, { payload }: { payload: string[] }) => {
      if (payload?.sort().toString() !== state.skus?.sort().toString()) {
        state.skus = payload;
      }
    },
    setDeliveryOption: (state, { payload }) => {
      state.wSelectedDeliveryOption = { wdoCode: payload };
    },
    clearCartStates: (state) => {
      state.cartId = initialState.cartId;
      state.isLoading = false;
      state.isShowSpinner = false;
      state.istaxCalculated = STATUSES.FALSE;

      state.isCheckSubscriptions = STATUSES.FALSE;
      state.updatedWithNewCustomer = STATUSES.FALSE;
      state.updateBillingInformation = STATUSES.FALSE;

      state.error = null;
      state.isSubscriptionExist = false;

      state.orderPrice = initOrderPrice;
      state.billingInfo = initBillingInfo;
      state.orderItems = [];
      state.promotions = null;

      state.agreementCheck = false;
      state.pricPrice = { edges: [] };
      state.uid = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptionsForCheckout.pending, (state) => {
        state.isCheckSubscriptions = STATUSES.PENDING;
        state.isSubscriptionExist = false;
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSubscriptionsForCheckout.fulfilled, (state, { payload }) => {
        state.isCheckSubscriptions = STATUSES.TRUE;
        state.isSubscriptionExist = payload;
        state.isLoading = false;
      })
      .addCase(getSubscriptionsForCheckout.rejected, (state, { payload }) => {
        state.error = payload;
        state.isLoading = false;
      })

      .addCase(getCartData.pending, (state) => {
        state.isSubscriptionExist = false;
        state.isLoading = true;
        state.isShowSpinner = true;
        state.error = null;
      })
      .addCase(
        getCartData.fulfilled,
        (state, { payload: { response, language } }) => {
          const {
            biId,
            pricPrice,
            paybPayment,
            wContactPhone,
            paybBillingAddress,
            wTOSAccepted,
            prRelatedParties,
            hiConsistsOf,
            wSchoolFiceCode,
            corPromoCodes,
            orPlacementDate,
            biRelatesTo,
            wGuestOrder,
            wSelectedDeliveryOption,
            wSiteId,
            lastBillBiId,
            billFromOrder,
          } = response?.wGetCheckoutCart.result.order;

          if (wGuestOrder) {
            const isGuestAllowed = checkIsGuestAllowed(hiConsistsOf);
            const isConfirmationPage = window.location.pathname.includes(
              Constants.Pages.ORDER_CONFIRMATION
            );
            if (isConfirmationPage) clearTokensFromCookies();
            if (!isGuestAllowed) redirectGuestToAuth();
          }

          const { orderItemDiscounts } = response?.wGetCartDiscounts.discounts;
          const discounts = convertDiscounts(
            orderItemDiscounts,
            corPromoCodes,
            pricPrice
          );
          const isFreeTrial =
            orderItemDiscounts[0]?.discountType === DiscountType.FREETRIAL;
          const { isSubscriptionSite } = SiteConfig.getConfig();

          state.wSelectedDeliveryOption = wSelectedDeliveryOption;
          state.biId = biId;
          state.schoolFiceId = wSchoolFiceCode;
          state.isFreeTrial = isFreeTrial;
          state.orderPrice = getOrderPrice(pricPrice.edges);
          state.orderItems = getOrderItems(
            hiConsistsOf,
            language,
            isSubscriptionSite
          );
          state.nextBillDate = isSubscriptionSite
            ? getSubscriptionNextBillingDate(hiConsistsOf, discounts)
            : null;
          state.subscriptionFrequency = isSubscriptionSite
            ? getSubscriptionFrequency(hiConsistsOf)
            : null;
          state.agreementCheck = wTOSAccepted;
          state.prRelatedParties = prRelatedParties;
          state.isGuestOrder = wGuestOrder;
          state.isTextbook = state.orderItems.some((item) => item.isTextbook);
          state.isPhysical = state.orderItems.some(({ format }) => {
            return (
              format === DigitalFormat.PHYSICAL ||
              format === DigitalFormat.DIGITAL_AND_PHYSICAL
            );
          });
          state.isPrintOnDemand = state.orderItems.some(
            (item) => item.printOnDemand
          );
          state.discounts = discounts;
          state.paymentInfo = paybPayment;
          state.orPlacementDate = orPlacementDate;
          state.promotions = corPromoCodes;
          state.isSubscription = isSubscriptionSite;
          state.billingInfo = {
            name: paybPayment?.ccpaNameOnCard || '',
            tel: wContactPhone || '',
            ...mapToView(paybBillingAddress),
          };

          // TODO: move to the lambda side when this is implemented
          const savedShippingAddresses = biRelatesTo?.edges || [];
          savedShippingAddresses.sort(
            (a, b) =>
              new Date(b.node.createdAt).getTime() -
              new Date(a.node.createdAt).getTime()
          );

          const savedShippingAddress = savedShippingAddresses[0];

          state.shippingInfo = {
            ...mapShippingToView(
              savedShippingAddress?.node?.tadDeliveryAddress,
              savedShippingAddress?.node.uid
            ),
          };

          state.isShowSpinner = false;
          state.isLoading = false;
          state.isCartReady = true;
          state.updatedWithNewCustomer = STATUSES.TRUE;
          state.wSiteId = wSiteId;
          state.lastBillBiId =
            lastBillBiId ?? billFromOrder?.edges[0]?.node.biId;
        }
      )
      .addCase(getCartData.rejected, (state, { payload }) => {
        state.error = payload;
        state.updatedWithNewCustomer = STATUSES.FALSE;
        state.isShowSpinner = false;
        state.isLoading = false;
      })

      .addCase(getDeliveryOptions.fulfilled, (state, { payload }) => {
        const deliveryOptions = convertDeliveryOptions(payload);
        state.deliveryOptions = deliveryOptions;

        const wdoCode =
          deliveryOptions.find(
            ({ id }) => id === state.wSelectedDeliveryOption?.wdoCode
          )?.id || deliveryOptions[0]?.id;

        state.wSelectedDeliveryOption = { wdoCode };
      })

      .addCase(updateBillingInformation.pending, (state) => {
        state.error = null;
        state.isLoading = true;
        state.isShowSpinner = true;
        state.updateBillingInformation = STATUSES.PENDING;
      })
      .addCase(updateBillingInformation.fulfilled, (state, { payload }) => {
        state.orderPrice = payload.orderPrice;
        state.billingInfo = payload.billingInfo;

        state.isLoading = false;
        state.isShowSpinner = false;
        state.updateBillingInformation = STATUSES.TRUE;
      })
      .addCase(updateBillingInformation.rejected, (state, { payload }) => {
        state.error = payload;
        state.isLoading = false;
        state.isShowSpinner = false;
        state.updateBillingInformation = STATUSES.FALSE;
      })

      .addCase(updateShippingInformation.pending, (state) => {
        state.error = null;
        state.isLoading = true;
        state.isShowSpinner = true;
      })
      .addCase(updateShippingInformation.fulfilled, (state, { payload }) => {
        const {
          biRelatesTo,
          wSelectedDeliveryOption,
          deliDeliveryPeriod,
          pricPrice,
        } = payload;

        const deliveryOption = state.deliveryOptions.find(
          ({ id }) => id === wSelectedDeliveryOption?.wdoCode
        );

        if (deliveryOption) {
          deliveryOption.date = {
            from: deliDeliveryPeriod.start,
            to: deliDeliveryPeriod.end,
          };
        }

        state.isLoading = false;
        state.isShowSpinner = false;
        state.wSelectedDeliveryOption = wSelectedDeliveryOption;
        state.orderPrice = getOrderPrice(pricPrice.edges);
        state.shippingInfo = {
          ...mapShippingToView(
            biRelatesTo?.edges[0]?.node?.tadDeliveryAddress,
            biRelatesTo?.edges[0]?.node.uid,
            deliveryOption
          ),
        };
      })
      .addCase(updateShippingInformation.rejected, (state, { payload }) => {
        state.error = payload;
        state.isLoading = false;
        state.isShowSpinner = false;
      })
      .addCase(updateWpsPaymentInformation.pending, (state) => {
        state.error = null;
        state.isLoading = true;
        state.isShowSpinner = true;
        state.updatePaymenInformation = STATUSES.PENDING;
      })
      .addCase(updateWpsPaymentInformation.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isShowSpinner = false;
        state.updatePaymenInformation =
          payload === 'OK' ? STATUSES.TRUE : STATUSES.FALSE;
      })
      .addCase(updateWpsPaymentInformation.rejected, (state, { payload }) => {
        state.error = payload;
        state.isLoading = false;
        state.isShowSpinner = false;
      })

      .addCase(getTaxData.pending, (state) => {
        state.error = null;
        state.isLoading = true;
        state.isShowSpinner = true;
        state.istaxCalculated = STATUSES.PENDING;
      })
      .addCase(
        getTaxData.fulfilled,
        (state, { payload: { response, language } }) => {
          const { pricPrice, hiConsistsOf, paybPayment, wSiteId } =
            response?.wGetCheckoutCart.result.order;
          const { isSubscriptionSite } = SiteConfig.getConfig();

          state.isLoading = false;
          state.isShowSpinner = false;
          state.istaxCalculated = STATUSES.TRUE;
          state.orderItems = getOrderItems(
            hiConsistsOf,
            language,
            isSubscriptionSite
          );
          state.paymentInfo = paybPayment;
          state.isSubscription = isSubscriptionSite;
          state.orderPrice = getOrderPrice(pricPrice.edges);
          state.wSiteId = wSiteId;
        }
      )
      .addCase(getTaxData.rejected, (state, { payload }) => {
        state.error = payload;
      })
      .addCase(isCartHasUnavailableProducts.pending, (state) => {
        state.isLoading = true;
        state.isShowSpinner = true;
      })
      .addCase(isCartHasUnavailableProducts.fulfilled, (state, { payload }) => {
        if (payload) {
          window.location.href = SiteConfig.buildRedirectUrl(
            Constants.CART_CHECKOUT_BASE_URL,
            Constants.CART_PATH
          );
          return;
        }

        state.isLoading = false;
        state.isShowSpinner = false;
      })
      .addCase(isCartHasUnavailableProducts.rejected, (state) => {
        state.isLoading = false;
        state.isShowSpinner = false;
      });
  },
});

export const {
  setCartId,
  setSku,
  toggleAgreementCheck,
  clearCartStates,
  setUpdateBillingInformation,
  setDeliveryOption,
} = cartSlice.actions;

export const cartReducer = cartSlice.reducer;
